import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { adminApi, type SeatStatus, type UserSeat } from '@/lib/api';

/* ---- Display helpers (shared between Seats list + Users detail) ---- */

const STATUS_META: Record<SeatStatus, { label: string; variant: BadgeVariant }> = {
  active: { label: '正常', variant: 'success' },
  suspended: { label: '已暂停', variant: 'warning' },
  expired: { label: '已过期', variant: 'muted' },
  revoked: { label: '已删除', variant: 'destructive' },
};

export function SeatStatusBadge({ status }: { status: SeatStatus }) {
  const meta = STATUS_META[status] ?? STATUS_META.active;
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

const SOON_MS = 7 * 24 * 60 * 60 * 1000;

export function expiryMeta(validUntil: string): { expired: boolean; soon: boolean; className: string } {
  const ms = new Date(validUntil).getTime() - Date.now();
  const expired = ms <= 0;
  const soon = !expired && ms <= SOON_MS;
  return {
    expired,
    soon,
    className: expired ? 'text-destructive font-medium' : soon ? 'text-amber-600 font-medium' : '',
  };
}

export function seatRemaining(
  seat: Pick<UserSeat, 'credits_granted' | 'credits_used_cached' | 'status' | 'valid_until'>,
): number {
  if (effectiveSeatStatus(seat) === 'expired') return 0;
  return Number(seat.credits_granted) - Number(seat.credits_used_cached);
}

/**
 * Effective seat state shown to the admin: revoked / suspended take precedence,
 * then date-derived expiry (valid_until passed), else active. Nothing writes
 * status='expired' — expiry is computed — so the badge mirrors the date column
 * and the list's status filter. Action eligibility still uses the raw
 * `seat.status` (a date-expired-but-active seat can still be suspended/deleted).
 */
export function effectiveSeatStatus(seat: Pick<UserSeat, 'status' | 'valid_until'>): SeatStatus {
  if (seat.status === 'revoked' || seat.status === 'suspended') return seat.status;
  if (new Date(seat.valid_until).getTime() <= Date.now()) return 'expired';
  return 'active';
}

/* ---- Action cell ----
 * Closed state is just a tidy row of buttons. Renew / adjust / release / delete
 * each open a modal dialog (one at a time). Suspend / resume are direct,
 * reversible toggles. Errors surface via the global toast; success toasts here.
 */

type Panel = 'renew' | 'adjust' | 'release' | 'delete' | 'suspend' | 'channel';

const BTN = 'h-7 px-2 text-xs';
/** Radix Select forbids an empty-string item value; sentinel for "无渠道". */
const NO_CHANNEL = '__none__';

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

export function SeatActionsCell({ seat, onChanged }: { seat: UserSeat; onChanged: () => void }) {
  const [panel, setPanel] = useState<Panel | null>(null);
  const toggle = (p: Panel) => setPanel((cur) => (cur === p ? null : p));
  const done = (msg: string) => {
    setPanel(null);
    onChanged();
    toast.success(msg);
  };
  const defaultCreditsQuery = useQuery({
    queryKey: ['settings', 'default-seat-credits'],
    queryFn: adminApi.getDefaultSeatCredits,
  });
  const defaultCredits = defaultCreditsQuery.data ? String(defaultCreditsQuery.data.value) : '';

  // adjust credits
  const [mode, setMode] = useState<'grant' | 'deduct' | 'set'>('grant');
  const [amount, setAmount] = useState('');
  const [amountTouched, setAmountTouched] = useState(false);
  useEffect(() => {
    if (defaultCredits && !amountTouched) setAmount(defaultCredits);
  }, [amountTouched, defaultCredits]);
  const parsedAmount = Number(amount);
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount >= 0;
  const [note, setNote] = useState('');
  const adjust = useMutation({
    mutationFn: () =>
      adminApi.adjustSeatCredits(seat.id, { user_id: seat.user_id, mode, amount: parsedAmount, note: note.trim() }),
    onSuccess: () => {
      setNote('');
      done('已调额');
    },
  });

  // renew
  const [months, setMonths] = useState('1');
  const [renewCredits, setRenewCredits] = useState('');
  const [renewCreditsTouched, setRenewCreditsTouched] = useState(false);
  useEffect(() => {
    if (panel === 'renew' && defaultCredits && !renewCreditsTouched) setRenewCredits(defaultCredits);
  }, [defaultCredits, panel, renewCreditsTouched]);
  const renewCreditNumber = Number(renewCredits);
  const renewCreditsValid =
    !renewCredits.trim() || (Number.isFinite(renewCreditNumber) && renewCreditNumber > 0);
  const renew = useMutation({
    mutationFn: () =>
      adminApi.renewSeat(seat.id, {
        user_id: seat.user_id,
        months: Number(months),
        credits: renewCredits.trim() ? renewCreditNumber : undefined,
      }),
    onSuccess: () => {
      setRenewCredits('');
      setRenewCreditsTouched(false);
      done('已续费');
    },
  });

  // suspend / resume
  const canSuspend = seat.status === 'active';
  const canResume = seat.status === 'suspended';
  const setStatus = useMutation({
    mutationFn: (status: 'active' | 'suspended') => adminApi.setSeatStatus(seat.id, { user_id: seat.user_id, status }),
    onSuccess: (_d, status) => done(status === 'active' ? '已恢复' : '已暂停'),
  });

  // release binding
  const release = useMutation({
    mutationFn: () => {
      if (!seat.current_ip) throw new Error('seat_ip_not_bound');
      return adminApi.releaseSeatBinding(seat.id, {
        user_id: seat.user_id,
        ip_id: seat.current_ip.id,
        reason: 'admin_manual_release',
      });
    },
    onSuccess: () => done('已解绑'),
  });

  // delete (soft / revoke)
  const isDefault = seat.source === 'default';
  const isBound = !!seat.current_ip;
  // Everything except the (auto-recreated) default seat can be hard-deleted;
  // bound seats are auto-released first by the backend.
  const deletable = !isDefault;
  const deleteHint = isDefault ? '默认席位不可删除' : '';
  const del = useMutation({
    mutationFn: () => adminApi.deleteSeat(seat.id, { user_id: seat.user_id }),
    onSuccess: () => done('已删除'),
  });

  // change channel — active channels (plus the seat's current one if it's since
  // been deactivated, so the dropdown can still show the existing value).
  const channelsQuery = useQuery({
    queryKey: ['admin', 'channels', 'active'],
    queryFn: () => adminApi.listChannels(),
    staleTime: 5 * 60_000,
  });
  const channelList = channelsQuery.data ?? [];
  const channelOptions =
    seat.channel && !channelList.some((c) => c.id === seat.channel!.id)
      ? [seat.channel, ...channelList]
      : channelList;
  const [channelId, setChannelId] = useState<string>(seat.channel_id ?? NO_CHANNEL);
  const openChannel = () => {
    setChannelId(seat.channel_id ?? NO_CHANNEL);
    toggle('channel');
  };
  const changeChannel = useMutation({
    mutationFn: () =>
      adminApi.setSeatChannel(seat.id, {
        user_id: seat.user_id,
        channel_id: channelId === NO_CHANNEL ? null : channelId,
      }),
    onSuccess: () => done('已修改渠道'),
  });

  const busy =
    adjust.isPending || renew.isPending || setStatus.isPending || release.isPending || del.isPending || changeChannel.isPending;

  return (
    <div className="min-w-[260px]">
      <div className="flex flex-wrap items-center gap-1">
        <Button size="sm" variant={panel === 'renew' ? 'default' : 'outline'} className={BTN} disabled={busy} onClick={() => toggle('renew')}>
          续费
        </Button>
        {canSuspend && (
          <Button size="sm" variant={panel === 'suspend' ? 'default' : 'outline'} className={BTN} disabled={busy} onClick={() => toggle('suspend')}>
            暂停
          </Button>
        )}
        {canResume && (
          <Button size="sm" variant="outline" className={BTN} disabled={busy} onClick={() => setStatus.mutate('active')}>
            恢复
          </Button>
        )}
        {isBound && (
          <Button size="sm" variant={panel === 'release' ? 'default' : 'outline'} className={BTN} disabled={busy} onClick={() => toggle('release')}>
            解绑
          </Button>
        )}
        <Button size="sm" variant={panel === 'adjust' ? 'default' : 'outline'} className={BTN} disabled={busy} onClick={() => toggle('adjust')}>
          调额
        </Button>
        <Button size="sm" variant={panel === 'channel' ? 'default' : 'outline'} className={BTN} disabled={busy} onClick={openChannel}>
          改渠道
        </Button>
        <Button
          size="sm"
          variant="outline"
          className={`${BTN} border-destructive/40 text-destructive hover:bg-destructive/10`}
          disabled={busy || !deletable}
          title={deleteHint}
          onClick={() => toggle('delete')}
        >
          删除
        </Button>
      </div>

      <Dialog
        open={panel === 'renew'}
        onClose={() => setPanel(null)}
        title="续费席位"
        description="延长有效期并补发月度算力；过期席位会清零旧周期未用算力后重新发放。"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPanel(null)}>取消</Button>
            <Button disabled={renew.isPending || !(Number(months) >= 1) || !renewCreditsValid} onClick={() => renew.mutate()}>确认续费</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label="延长时长（月）">
            <Input type="number" min={1} max={36} value={months} onChange={(e) => setMonths(e.target.value)} />
          </Field>
          <Field label="每月补发算力（留空使用默认）">
            <Input
              type="number"
              value={renewCredits}
              onChange={(e) => {
                setRenewCreditsTouched(true);
                setRenewCredits(e.target.value);
              }}
              placeholder={defaultCredits || '默认算力'}
            />
          </Field>
        </div>
      </Dialog>

      <Dialog
        open={panel === 'suspend'}
        onClose={() => setPanel(null)}
        title="暂停席位"
        description="暂停后该席位不可用于计费，可随时「恢复」。"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPanel(null)}>取消</Button>
            <Button disabled={setStatus.isPending} onClick={() => setStatus.mutate('suspended')}>确认暂停</Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">暂停期间该席位的算力无法被消耗；恢复后立即可用。</p>
      </Dialog>

      <Dialog
        open={panel === 'adjust'}
        onClose={() => setPanel(null)}
        title="调整算力"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPanel(null)}>取消</Button>
            <Button disabled={!amountValid || !note.trim() || adjust.isPending} onClick={() => adjust.mutate()}>保存</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label="方式与数值">
            <div className="flex items-center gap-2">
              <Select value={mode} onValueChange={(v) => setMode(v as typeof mode)}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="grant">增加</SelectItem>
                  <SelectItem value="deduct">扣减</SelectItem>
                  <SelectItem value="set">设置为</SelectItem>
                </SelectContent>
              </Select>
              <Input
                className="flex-1"
                type="number"
                value={amount}
                onChange={(e) => {
                  setAmountTouched(true);
                  setAmount(e.target.value);
                }}
                placeholder={defaultCredits || '默认算力'}
              />
            </div>
          </Field>
          <Field label="调额原因（必填）">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="例如：客户充值 / 异常扣减修正" />
          </Field>
        </div>
      </Dialog>

      <Dialog
        open={panel === 'channel'}
        onClose={() => setPanel(null)}
        title="修改销售渠道"
        description="变更该席位归属的销售渠道；用户账户中心显示的渠道随之更新。"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPanel(null)}>取消</Button>
            <Button
              disabled={changeChannel.isPending || channelId === (seat.channel_id ?? NO_CHANNEL)}
              onClick={() => changeChannel.mutate()}
            >
              保存
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label="销售渠道">
            <Select value={channelId} onValueChange={setChannelId} disabled={channelsQuery.isLoading}>
              <SelectTrigger>
                <SelectValue placeholder={channelsQuery.isLoading ? '加载中…' : '选择渠道'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CHANNEL}>无渠道</SelectItem>
                {channelOptions.map((ch) => (
                  <SelectItem key={ch.id} value={ch.id}>
                    {ch.name}
                    {ch.is_active === false ? '（已停用）' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </Dialog>

      <Dialog
        open={panel === 'release' && !!seat.current_ip}
        onClose={() => setPanel(null)}
        title="解绑当前 IP"
        description={seat.current_ip ? `当前绑定 IP「${seat.current_ip.name}」。` : undefined}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPanel(null)}>取消</Button>
            <Button disabled={release.isPending} onClick={() => release.mutate()}>确认解绑</Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">解绑后该席位将空出、可重新绑定其他 IP；该 IP 会被标记为已删除。</p>
      </Dialog>

      <Dialog
        open={panel === 'delete' && deletable}
        onClose={() => setPanel(null)}
        title="删除席位"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPanel(null)}>取消</Button>
            <Button variant="destructive" disabled={del.isPending} onClick={() => del.mutate()}>确认删除</Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          确认<b className="text-foreground">永久删除</b>该席位？
          {isBound && `当前绑定的 IP「${seat.current_ip?.name}」会先自动解绑回收。`}
          此操作<b className="text-foreground">不可恢复</b>——该席位的调额台账与绑定历史一并删除（使用计费记录保留但解除关联）；删除事件记入审计日志。
        </p>
      </Dialog>
    </div>
  );
}
